import { matchesNestedKeyword } from '../../shared/utils/keyword-filter';
import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberApiService } from '../../core/services/member-api.service';
import { StudioService } from '../../core/services/studio.service';
import { TransactionService } from '../../core/services/transaction.service';

const INVOICE_CREATE_DATE = 12;
const INVOICE_CREATE_HOUR = 2;

@Component({
    selector: 'app-transaction',
    imports: [CommonModule, FormsModule, CurrencyPipe, DatePipe],
    templateUrl: './transaction.component.html'
})
export class TransactionComponent implements OnInit {
  private pageTitle = inject(PageTitleService);
  private memberApi = inject(MemberApiService);
  private studioService = inject(StudioService);
  private transactionService = inject(TransactionService);

  member: any = {};
  studio: any = {};
  items: any[] = [];
  filteredItems: any[] = [];

  yearPayOption: { id: number; name: string }[] = [];
  monthPayOption: { id: number; name: string }[] = [];
  statusOption = [
    { id: '1', name: '削除済' },
    { id: '2', name: '請求確定' },
    { id: '5', name: '入金済' },
    { id: '6', name: '未納' },
    { id: '7', name: '払い戻し済' },
    { id: '8', name: '請求停止' },
    { id: '9', name: '受け取り' },
  ];

  selectYear: { id: number; name: string } | null = null;
  selectMonth: { id: number; name: string } | null = null;
  selectSettleMonth = '';
  statusFilter: { id: string; name: string } | null = null;
  keyword = '';

  pageLimit = 50;
  limitBegin = 0;

  showListTable = false;
  searching = true;

  singleTransaction: any = {};
  receiptInput: any = {};

  settlementList: any = {};
  isBeforeCreateInvoice = false;
  thisIssueMonth = '';

  async ngOnInit(): Promise<void> {
    this.pageTitle.set('fa s-icon-list', 'お支払一覧');
    const now = new JstDate();
    this.yearPayOption = this.getYearList(2015, now.getFullYear() + 1);
    this.monthPayOption = this.getMonthList();
    this.setSettlementList(now);
    this.isBeforeCreateInvoice = this.getIsBeforeCreateInvoice(now);
    this.thisIssueMonth = this.getThisIssueMonth(now);
    await this.updateList();
  }

  private getYearList(start: number, end: number) {
    const list = [];
    for (let i = start; i <= end; i++) list.push({ id: i, name: `${i}年` });
    return list;
  }

  private getMonthList() {
    const list = [];
    for (let i = 1; i <= 12; i++) list.push({ id: i, name: `${i}月` });
    return list;
  }

  private getIsBeforeCreateInvoice(now: Date): boolean {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const threshold = new JstDate(year, month - 1, INVOICE_CREATE_DATE, INVOICE_CREATE_HOUR);
    return now < threshold;
  }

  private getThisIssueMonth(now: Date): string {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return `${year}-${String(month).padStart(2, '0')}`;
  }

  private setSettlementList(now: Date): void {
    const thisYear = now.getFullYear();
    const thisMonth = now.getMonth();
    this.settlementList = {
      thisMonthAccountTransfer: new JstDate(thisYear, thisMonth, 28),
      nextMonthAccountTransfer: new JstDate(thisYear, thisMonth + 1, 28),
      nextMonthCreditCard: new JstDate(thisYear, thisMonth + 1, 1),
      theMonthAfterNextCreditCard: new JstDate(thisYear, thisMonth + 2, 1),
      thisYear,
      thisMonth: thisMonth + 1,
    };
  }

  private getSettlementDate(item: any): void {
    const { paymentMethod, yearPay, monthPay } = item;
    const createdAt = new JstDate(item.createdAt);
    const sl = this.settlementList;

    const thisMonth = [sl.thisYear, sl.thisMonth];
    const nextMonth = [
      thisMonth[1] === 12 ? thisMonth[0] + 1 : thisMonth[0],
      thisMonth[1] === 12 ? 1 : thisMonth[1] + 1,
    ];
    const lastMonth = [
      thisMonth[1] === 1 ? thisMonth[0] - 1 : thisMonth[0],
      thisMonth[1] === 1 ? 12 : thisMonth[1] - 1,
    ];

    let settlementDate: Date | '-' = '-';

    if (item.status === 2 && !this.isBeforeCreateInvoice) {
      const issueMonth = item.invoice.issueMonth.split('-');
      settlementDate = paymentMethod === 0
        ? new JstDate(parseInt(issueMonth[0]), parseInt(issueMonth[1]) - 1, 28)
        : new JstDate(parseInt(issueMonth[0]), parseInt(issueMonth[1]), 1);
    } else if (
      this.isBeforeCreateInvoice
      && (item.status === 0 || item.status === 3)
      && yearPay === thisMonth[0] && monthPay === thisMonth[1]
    ) {
      settlementDate = paymentMethod === 0
        ? new JstDate(thisMonth[0], thisMonth[1] - 1, 28)
        : new JstDate(nextMonth[0], nextMonth[1] - 1, 1);
    } else if (
      this.isBeforeCreateInvoice
      && (item.status === 0 || item.status === 3)
      && item.isAutoGenerate === 0
    ) {
      if (yearPay === nextMonth[0] && monthPay === nextMonth[1]) {
        settlementDate = paymentMethod === 0
          ? new JstDate(thisMonth[0], thisMonth[1] - 1, 28)
          : new JstDate(nextMonth[0], nextMonth[1] - 1, 1);
      }
    } else if (
      this.isBeforeCreateInvoice
      && (item.status === 0 || item.status === 3)
      && item.isAutoGenerate === 1
    ) {
      const startDate = new JstDate(lastMonth[0], lastMonth[1] - 1, 1);
      const endDate = new JstDate(thisMonth[0], thisMonth[1] - 1, 1);
      if (createdAt >= startDate && createdAt < endDate) {
        settlementDate = paymentMethod === 0
          ? new JstDate(thisMonth[0], thisMonth[1] - 1, 28)
          : new JstDate(nextMonth[0], nextMonth[1] - 1, 1);
      }
    } else if (
      !this.isBeforeCreateInvoice
      && (item.status === 0 || item.status === 3)
    ) {
      const startDate = new JstDate(thisMonth[0], thisMonth[1] - 1, 1);
      const endDate = new JstDate(nextMonth[0], nextMonth[1] - 1, 1);
      if (
        (yearPay === nextMonth[0] && monthPay === nextMonth[1])
        || (createdAt >= startDate && createdAt < endDate && item.isAutoGenerate === 1)
      ) {
        settlementDate = paymentMethod === 0
          ? new JstDate(nextMonth[0], nextMonth[1] - 1, 28)
          : new JstDate(nextMonth[0], nextMonth[1], 1);
      }
    }

    if (settlementDate === '-') {
      item.settlementMonth = '-';
    } else {
      const m = (settlementDate as Date).getMonth() + 1;
      const y = (settlementDate as Date).getFullYear();
      item.settlementMonth = `${y}年${m}月`;
    }
  }

  async updateList(): Promise<void> {
    const [memberData, transactions] = await Promise.all([
      this.memberApi.getOne('self'),
      this.memberApi.getTransaction('self'),
    ]);
    this.member = memberData;
    if (memberData?.studio?.id) {
      this.studio = await this.studioService.list(memberData.studio.id) ?? {};
    }
    this.searching = false;
    this.items = Array.isArray(transactions) ? transactions : [];
    this.items.forEach(v => this.getSettlementDate(v));
    this.showListTable = this.items.length > 0;
    this.applyFilter();
  }

  applyFilter(): void {
    this.limitBegin = 0;
    this.filteredItems = this.items.filter(item => {
      if (this.statusFilter && String(item.status) !== this.statusFilter.id) return false;
      if (
        this.selectSettleMonth
        && !String(item.settleDate ?? '').includes(this.selectSettleMonth)
      ) return false;
      if (!matchesNestedKeyword(item, this.keyword)) return false;
      return true;
    });
  }

  clearFilter(): void {
    this.selectYear = null;
    this.selectMonth = null;
    this.selectSettleMonth = '';
    this.keyword = '';
    this.statusFilter = null;
    this.applyFilter();
  }

  updateSelectSettleMonth(): void {
    this.selectSettleMonth = '';
    if (this.selectYear !== null) this.selectSettleMonth += `${this.selectYear.id}年`;
    if (this.selectMonth !== null) this.selectSettleMonth += `${this.selectMonth.id}月`;
    this.applyFilter();
  }

  get pageCount(): number {
    return Math.ceil(this.filteredItems.length / this.pageLimit);
  }

  get pagedItems(): any[] {
    return this.filteredItems.slice(this.limitBegin, this.limitBegin + this.pageLimit);
  }

  get pagerPages(): number[] {
    return Array.from({ length: this.pageCount }, (_, i) => i);
  }

  get currentPage(): number {
    return Math.floor(this.limitBegin / this.pageLimit);
  }

  pagerClick(num: number): void {
    this.limitBegin = num * this.pageLimit;
  }

  getReceipt(transaction: any): void {
    this.receiptInput = {};
    this.singleTransaction = transaction;
  }

  async downloadReceipt(transactionId: number, memberCode: string): Promise<void> {
    const fileName = String(memberCode).replace('-', '') + String(transactionId) + '.pdf';
    const html2canvas = (window as any).html2canvas;
    const jsPDF = (window as any).jspdf?.jsPDF || (window as any).jsPDF;
    const canvas = await html2canvas(document.getElementById('transactionReceipt'), {
      backgroundColor: '#FFFFFF',
      width: 500,
      height: 800,
      scrollX: 0,
      scrollY: 0,
    });
    await this.transactionService.downloadedReceipt({ id: transactionId, isDownloadedReceipt: 1 });
    const target = this.items.find(item => item.id === transactionId);
    if (target) target.isDownloadedReceipt = '1';
    const pdf = new jsPDF('p', 'pt', 'a4', false);
    const width = pdf.internal.pageSize.width;
    pdf.addImage(canvas, 'PNG', 0, 0, width, 0);
    pdf.save(fileName);
  }

  formatCurrency(val: number): string {
    return `¥${val?.toLocaleString('ja-JP') ?? 0}`;
  }
}
